import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { formatarReais } from '@/lib/formato';
import { explicarFalha, pedir } from '@/lib/pedido';

/**
 * O CUPOM DO POSTO LIDO PELA IA (decisão 14).
 *
 * A IA lê a foto do cupom e PREENCHE o lançamento externo: posto, produto,
 * data, litros, preço e número. Nada é gravado aqui — quem lança confere os
 * campos marcados como preenchidos e grava pelo botão de sempre (decisão 12).
 * O que a leitura não conseguiu fechar aparece escrito, e não só colorido.
 */

export interface CupomLido {
  readonly postoId: string | null;
  readonly postoEscrito: string | null;
  readonly produtoId: string | null;
  readonly produtoEscrito: string | null;
  readonly data: string | null;
  readonly hora: string | null;
  readonly litros: number | null;
  readonly precoLitro: number | null;
  readonly total: number | null;
  readonly numero: string | null;
  readonly totalConfere: boolean | null;
  readonly duvidas: readonly string[];
}

const NOME_DO_CAMPO: Readonly<Record<string, string>> = {
  posto: 'posto',
  produto: 'produto',
  data: 'data',
  hora: 'hora',
  litros: 'litros',
  preco_litro: 'preço do litro',
  total: 'total',
  numero: 'número do cupom',
};

const lerCupom = (arquivo: File) =>
  pedir<{ cupom: CupomLido }>('/combustivel/cupons/leitura', {
    method: 'POST',
    body: arquivo,
    headers: { 'content-type': arquivo.type || 'application/octet-stream' },
  });

export function LeitorDeCupom({ aoLer }: { readonly aoLer: (cupom: CupomLido) => void }) {
  const base = useId();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const ler = useMutation({
    mutationFn: (f: File) => lerCupom(f),
    /* Ler não grava nada: o formulário abaixo se preenche, e as dúvidas aparecem aqui. */
    meta: { silencioso: true },
    onSuccess: (r) => {
      aoLer(r.cupom);
    },
  });
  const cupom = ler.data?.cupom ?? null;
  const duvidas = (cupom?.duvidas ?? []).map((d) => NOME_DO_CAMPO[d] ?? d);

  return (
    <div className="flex flex-col gap-3 rounded-controle border border-borda-sutil p-4">
      <Campo
        id={`${base}-cupom`}
        rotulo="Cupom do posto"
        opcional
        ajuda="Foto ou escaneamento do cupom fiscal. A IA preenche o lançamento abaixo, e você confere antes de lançar."
      >
        <input
          id={`${base}-cupom`}
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          className="controle w-full"
          onChange={(e) => {
            setArquivo(e.target.files?.[0] ?? null);
          }}
        />
      </Campo>
      <div>
        {/* Consulta à IA (R02, p.31): ler o cupom só preenche, não grava. */}
        <Botao
          nivel="consulta"
          disabled={arquivo === null || ler.isPending}
          onClick={() => {
            if (arquivo) ler.mutate(arquivo);
          }}
        >
          {ler.isPending ? 'Lendo o cupom…' : 'Ler cupom pela IA'}
        </Botao>
      </div>
      {ler.isError ? (
        <p role="alert" className="txt-apoio text-parada">{`${explicarFalha(ler.error).erro}. ${explicarFalha(ler.error).comoResolver}`}</p>
      ) : null}
      {/* A IA lendo (p.32): o lugar do resultado vira esqueleto na forma dele —
          a frase do cupom lido e a do que conferir. O botão diz "Lendo o
          cupom…"; quem lê a tela ouve a mesma coisa daqui. */}
      {ler.isPending ? (
        <Carregando oQue="Lendo o cupom" className="flex flex-col gap-2 py-1">
          <Barra className="h-3 w-full" />
          <Barra className="h-3 w-4/5" />
          <Barra className="h-3 w-1/2" />
        </Carregando>
      ) : null}
      {cupom ? (
        <div role="status" className="flex flex-col gap-1">
          <p className="txt-apoio text-tinta-secundaria">
            {`Cupom lido${cupom.postoEscrito ? ` de ${cupom.postoEscrito}` : ''}${cupom.total !== null ? `, total de ${formatarReais(cupom.total)}` : ''}. Os campos preenchidos pela leitura estão marcados: confira cada um antes de lançar.`}
          </p>
          {cupom.totalConfere === false ? (
            <p className="txt-apoio text-tinta">
              A conta do cupom não fecha: litros vezes preço não dá o total impresso. Confira os três no papel.
            </p>
          ) : null}
          {!cupom.postoId && cupom.postoEscrito ? (
            <p className="txt-apoio text-tinta">
              {`O posto “${cupom.postoEscrito}” não casou com nenhum cadastro: escolha o posto à mão, ou cadastre-o com o CNPJ do fornecedor.`}
            </p>
          ) : null}
          {duvidas.length > 0 ? <p className="txt-apoio text-tinta">{`Confira com atenção: ${duvidas.join(', ')}.`}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
